/**
 * Search analytics sessionId (frontend-generated).
 * Lives in shared-lib so logout clearAllStorage can use it without
 * importing virtco (avoids circular deps / HMR "call of undefined").
 */

export const SEARCH_SESSION_STORAGE_KEY = 'virtco_search_session_id';
export const SEARCH_SESSION_META_KEY = 'virtco_search_session_meta';
export const SEARCH_ACTIVITY_LOG_ID_KEY = 'virtco_search_activity_log_id';
/** Stable per browser. Survives refresh and logout so guest search order stays on this system. */
export const SEARCH_DEVICE_ID_KEY = 'virtco_search_device_id';

/** Anonymous + authenticated: reuse same sessionId for this duration */
export const SEARCH_SESSION_TTL_MS = 60 * 60 * 1000; // 1 hour

/** Analytics keyword commitment: pause longer than this commits the search */
export const SEARCH_ANALYTICS_COMMIT_MS = 1000;

export interface SearchSessionMeta {
  id: string;
  createdAt: number;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export const isValidSearchSessionId = (id?: string | null): id is string =>
  !!id && id !== EMPTY_GUID;

const createGuid = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const readSessionMeta = (): SearchSessionMeta | null => {
  try {
    const raw = localStorage.getItem(SEARCH_SESSION_META_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as SearchSessionMeta;
      if (isValidSearchSessionId(parsed?.id) && typeof parsed.createdAt === 'number') {
        return parsed;
      }
    }
    const legacy = sessionStorage.getItem(SEARCH_SESSION_STORAGE_KEY);
    if (isValidSearchSessionId(legacy)) {
      const migrated: SearchSessionMeta = { id: legacy, createdAt: Date.now() };
      localStorage.setItem(SEARCH_SESSION_META_KEY, JSON.stringify(migrated));
      localStorage.setItem(SEARCH_SESSION_STORAGE_KEY, migrated.id);
      sessionStorage.removeItem(SEARCH_SESSION_STORAGE_KEY);
      return migrated;
    }
  } catch {
    // ignore
  }
  return null;
};

const writeSessionMeta = (meta: SearchSessionMeta) => {
  try {
    localStorage.setItem(SEARCH_SESSION_META_KEY, JSON.stringify(meta));
    localStorage.setItem(SEARCH_SESSION_STORAGE_KEY, meta.id);
  } catch {
    // ignore
  }
};

export const clearSearchActivityLogId = (): void => {
  try {
    localStorage.removeItem(SEARCH_ACTIVITY_LOG_ID_KEY);
    sessionStorage.removeItem(SEARCH_ACTIVITY_LOG_ID_KEY);
  } catch {
    // ignore
  }
};

const DEVICE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const readDeviceCookie = (): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + SEARCH_DEVICE_ID_KEY + '=([^;]*)'));
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
};

const writeDeviceCookie = (id: string): void => {
  if (typeof document === 'undefined') return;
  document.cookie = `${SEARCH_DEVICE_ID_KEY}=${encodeURIComponent(id)};path=/;max-age=${DEVICE_COOKIE_MAX_AGE_SECONDS};SameSite=Lax`;
};

/**
 * One id per browser profile.
 * Refresh, new tab, and logout keep it. A different browser or system gets a new id.
 */
export const getOrCreateSearchDeviceId = (): string => {
  try {
    const fromCookie = readDeviceCookie();
    if (isValidSearchSessionId(fromCookie)) {
      localStorage.setItem(SEARCH_DEVICE_ID_KEY, fromCookie);
      return fromCookie;
    }

    const fromStorage = localStorage.getItem(SEARCH_DEVICE_ID_KEY);
    if (isValidSearchSessionId(fromStorage)) {
      writeDeviceCookie(fromStorage);
      return fromStorage;
    }

    const id = createGuid();
    localStorage.setItem(SEARCH_DEVICE_ID_KEY, id);
    writeDeviceCookie(id);
    return id;
  } catch {
    const fromCookie = readDeviceCookie();
    if (isValidSearchSessionId(fromCookie)) return fromCookie;
    const id = createGuid();
    writeDeviceCookie(id);
    return id;
  }
};

/**
 * Frontend-generated analytics sessionId.
 * - Reused for up to 1 hour (anonymous and logged-in)
 * - New id after TTL expires
 * - Cleared on logout via resetSearchAnalyticsSession()
 */
export const getOrCreateSearchSessionId = (): string => {
  try {
    const existing = readSessionMeta();
    const now = Date.now();
    if (existing && now - existing.createdAt < SEARCH_SESSION_TTL_MS) {
      return existing.id;
    }
    const next: SearchSessionMeta = { id: createGuid(), createdAt: now };
    writeSessionMeta(next);
    clearSearchActivityLogId();
    return next.id;
  } catch {
    return createGuid();
  }
};

/** Call on logout so the next visit starts a fresh analytics session */
export const resetSearchAnalyticsSession = (): void => {
  try {
    localStorage.removeItem(SEARCH_SESSION_META_KEY);
    localStorage.removeItem(SEARCH_SESSION_STORAGE_KEY);
    localStorage.removeItem(SEARCH_ACTIVITY_LOG_ID_KEY);
    sessionStorage.removeItem(SEARCH_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(SEARCH_ACTIVITY_LOG_ID_KEY);
  } catch {
    // ignore
  }
};

export const persistSearchActivityLogId = (logId: string | null): void => {
  try {
    if (logId) {
      localStorage.setItem(SEARCH_ACTIVITY_LOG_ID_KEY, logId);
      sessionStorage.setItem(SEARCH_ACTIVITY_LOG_ID_KEY, logId);
    } else {
      clearSearchActivityLogId();
    }
  } catch {
    // ignore
  }
};

export const readPersistedSearchActivityLogId = (): string | null => {
  try {
    return (
      localStorage.getItem(SEARCH_ACTIVITY_LOG_ID_KEY) ||
      sessionStorage.getItem(SEARCH_ACTIVITY_LOG_ID_KEY)
    );
  } catch {
    return null;
  }
};
